import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, FormGroup, Label, Input, Button } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { ToolfaceDial, toolfaceColour, toolfaceLabel } from '../widgets/ToolfaceDial';

// Public live view of a running Navigator steering session. The operator starts a remote
// session in Navigator under a short code and sends it on; anyone holding the code can watch
// here. Read-only, latest reading only - see RemoteController for the relay itself.
//
// The page's own wording is translated. The toolface names and the mode are not: they are
// the words on Navigator's steering screen, which is English, and the whole point of this
// page is that the watcher reads what the operator reads.

const POLL_MS = 1000;
const RETRY_MS = 2000;
const REQUEST_TIMEOUT_MS = 5000;
// A deploy, an instance recycle or a blip on rig internet all look like a 404 for a moment.
// Showing "no such session" immediately would send people hunting for a typo that isn't
// there, so hold off until it has persisted.
const NOT_FOUND_GRACE_MS = 15000;

const VALID_CODE = /^[A-Za-z0-9][A-Za-z0-9_-]{3,31}$/;

const formatNumber = (value, decimals = 2) =>
    (value === null || value === undefined || isNaN(value)) ? '--' : Number(value).toFixed(decimals);

// Navigator shows tool temperature in both units; match it, so the two screens read the same.
const formatTemperature = (celsius) => {
    if (celsius === null || celsius === undefined || isNaN(celsius)) return '--';
    const c = Number(celsius);
    return `${c.toFixed(1)}°C / ${(c * 9 / 5 + 32).toFixed(1)}°F`;
};

// A readout in Navigator's shape: small dark label over a large number on a light field.
// The value's colour is always stated, never inherited - inheriting is what made these
// unreadable in the first place.
const Reading = ({ label, value, unit, cols = 'col-12', accent = '#212529', size = 26, active = false }) => (
    <div className={`${cols} mb-3`}>
        <div
            className="bg-light rounded p-2 h-100 text-center"
            style={active ? { boxShadow: `inset 0 0 0 2px ${accent}` } : undefined}
        >
            <div className="text-uppercase fw-semibold" style={{ fontSize: 11, color: '#5a6068', letterSpacing: '0.5px' }}>{label}</div>
            <div className="fw-semibold" style={{ fontSize: size, color: accent, lineHeight: 1.2 }}>
                {value}{unit && <span style={{ fontSize: 13, color: '#7a8087', marginLeft: 2 }}>{unit}</span>}
            </div>
        </div>
    </div>
);

export const RemoteViewer = () => {
    const { t } = useTranslation();
    const { sessionId } = useParams();
    const navigate = useNavigate();

    const [code, setCode] = useState('');
    const [codeError, setCodeError] = useState('');
    const [reading, setReading] = useState(null);
    const [status, setStatus] = useState('connecting');
    const [error, setError] = useState('');

    useEffect(() => {
        if (!sessionId) return undefined;

        // A generation token rather than a plain cancelled flag: resuming from a hidden tab
        // restarts the loop immediately, and without this the in-flight request would go on
        // to schedule a poll of its own and we'd end up with two loops running.
        let generation = 0;
        let timer = null;
        let controller = null;
        let missingSince = null;
        let stopped = false;

        const schedule = (delay, gen) => {
            if (stopped || gen !== generation) return;
            timer = setTimeout(() => poll(gen), delay);
        };

        const poll = async (gen) => {
            if (stopped || gen !== generation) return;
            if (document.visibilityState === 'hidden') {
                schedule(POLL_MS, gen);
                return;
            }

            controller = new AbortController();
            const abortTimer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
            try {
                const response = await fetch(`api/remote/${encodeURIComponent(sessionId)}`, { signal: controller.signal });
                clearTimeout(abortTimer);
                if (stopped || gen !== generation) return;

                if (response.status === 404) {
                    if (missingSince === null) missingSince = performance.now();
                    setStatus(performance.now() - missingSince > NOT_FOUND_GRACE_MS ? 'notfound' : 'reconnecting');
                    setError('');
                    schedule(POLL_MS, gen);
                    return;
                }
                if (!response.ok) throw new Error(`HTTP ${response.status}`);

                const payload = await response.json();
                if (stopped || gen !== generation) return;
                missingSince = null;
                setReading(payload);
                setStatus(payload.stale ? 'stale' : 'live');
                setError('');
                schedule(POLL_MS, gen);
            } catch (err) {
                clearTimeout(abortTimer);
                if (stopped || gen !== generation) return;
                setStatus('reconnecting');
                // The shared instance rather than the hook's t, so the polling loop does not
                // restart every time the language changes.
                setError(err.name === 'AbortError' ? i18n.t('remote.requestTimedOut') : err.message);
                schedule(RETRY_MS, gen);
            }
        };

        const restart = () => {
            if (stopped || document.visibilityState !== 'visible') return;
            generation += 1;
            clearTimeout(timer);
            if (controller) controller.abort();
            // Fetch straight away rather than waiting a tick - a phone propped on a dash
            // locks its screen constantly, and a stale number on wake looks like a fault.
            poll(generation);
        };

        document.addEventListener('visibilitychange', restart);
        setStatus('connecting');
        setReading(null);
        poll(generation);

        return () => {
            stopped = true;
            clearTimeout(timer);
            if (controller) controller.abort();
            document.removeEventListener('visibilitychange', restart);
        };
    }, [sessionId]);

    const submitCode = (e) => {
        e.preventDefault();
        const trimmed = code.trim();
        if (!VALID_CODE.test(trimmed)) {
            setCodeError(t('remote.invalidCode'));
            return;
        }
        setCodeError('');
        navigate(`/remote/${trimmed.toUpperCase()}`);
    };

    if (!sessionId) {
        return (
            <div className="container" style={{ maxWidth: 520 }}>
                <h1 className="mb-3">{t('remote.title')}</h1>
                <p className="text-muted">
                    {t('remote.enterCode')}
                </p>
                <Form onSubmit={submitCode}>
                    {codeError && <div className="alert alert-danger">{codeError}</div>}
                    <FormGroup>
                        <Label for="sessionCode">{t('remote.sessionCode')}</Label>
                        <Input
                            type="text"
                            id="sessionCode"
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            placeholder={t('remote.codePlaceholder')}
                            autoComplete="off"
                            autoCapitalize="characters"
                            style={{ textTransform: 'uppercase', letterSpacing: '2px', fontSize: 20 }}
                        />
                    </FormGroup>
                    <Button color="primary" type="submit" disabled={!code.trim()}>{t('remote.watch')}</Button>
                </Form>
            </div>
        );
    }

    const toolfaceType = reading?.activeToolfaceType || 'Highside';
    const toolfaceValue = reading?.activeToolfaceValue ?? reading?.highSide;
    const mode = reading?.mode || 'Idle';
    const age = reading?.ageSeconds;
    // Two independent clocks: ageSeconds is measured by the server since the rig last posted
    // and says whether the link is up; readingAgeSeconds is measured by Navigator since the
    // reading last changed and says whether the tool is still producing. A frozen tool and a
    // dropped link must not look the same.
    const toolQuiet = reading?.readingAgeSeconds > 30;

    const statusText = {
        connecting: t('remote.connecting'),
        live: t('remote.live'),
        stale: t('remote.stale', { seconds: Math.round(age || 0) }),
        reconnecting: t('remote.reconnecting'),
        notfound: t('remote.notFound')
    }[status];

    const statusColour = { live: '#5a8f5a', stale: '#d4a84b', notfound: '#c45c4b' }[status] || '#a18d6b';

    return (
        <div className="container" style={{ maxWidth: 900 }}>
            <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
                <h1 className="mb-0">
                    {t('remote.title')} <span className="text-muted" style={{ letterSpacing: '2px' }}>{sessionId.toUpperCase()}</span>
                </h1>
                <Button color="link" onClick={() => navigate('/remote')}>{t('remote.changeCode')}</Button>
            </div>

            <div className="d-flex align-items-center flex-wrap gap-3 mb-3">
                <span className="d-flex align-items-center gap-2">
                    <span style={{ width: 12, height: 12, borderRadius: '50%', background: statusColour, display: 'inline-block' }} />
                    <span className="fw-medium">{statusText}</span>
                </span>
                {status !== 'notfound' && (
                    <span className="text-muted">{t('remote.mode')} <span className="fw-semibold text-dark">{mode}</span></span>
                )}
                {error && <span className="text-muted small">({error})</span>}
            </div>

            {status === 'notfound' ? (
                <div className="alert alert-warning">
                    {/* Two keys around the code rather than <Trans> with a value: the code comes
                        from the URL, and Trans would parse any tags inside it. */}
                    {t('remote.nothingPublishingBefore')} <strong>{sessionId.toUpperCase()}</strong>{t('remote.nothingPublishingAfter')}
                </div>
            ) : (
                /* A plain bordered panel rather than a .card: cards are themed solid red app-wide
                   (custom.css) with white text, which is what made these numbers unreadable. Same
                   call as ChartsTab.js - on a white surface the default text colour is already
                   right. Don't convert this back to a .card. */
                <div className="border rounded bg-white p-3">
                    <div className="row align-items-center">
                        <div className="col-md-4">
                            <div className="row">
                                <Reading cols="col-4 col-md-12" label={t('remote.inclination')} value={formatNumber(reading?.inclination)} unit="&deg;" />
                                <Reading cols="col-4 col-md-12" label={t('remote.azimuth')} value={formatNumber(reading?.azimuth)} unit="&deg;" />
                                <Reading cols="col-4 col-md-12" label={t('remote.temperature')} value={formatTemperature(reading?.temperature)} size={17} />
                            </div>
                        </div>
                        <div className="col-md-8 text-center">
                            <ToolfaceDial value={toolfaceValue} mode={toolfaceType} />
                            {/* The dial shows whichever toolface the tool is steering on, captioned
                                the way Navigator captions it. */}
                            <div className="d-flex align-items-center justify-content-center gap-2 mt-1 mb-3">
                                <span style={{ width: 12, height: 12, borderRadius: '50%', background: toolfaceColour(toolfaceType), display: 'inline-block' }} />
                                <span style={{ color: '#5a6068' }}>{toolfaceLabel(toolfaceType)}</span>
                                {reading?.isAutoMode && <span className="text-muted small">{t('remote.auto')}</span>}
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <Reading
                            cols="col-6" label="Gyro Toolface" unit="&deg;" size={30}
                            value={formatNumber(reading?.steeringAngle)}
                            accent={toolfaceColour('Steering')}
                            active={toolfaceType === 'Steering'}
                        />
                        <Reading
                            cols="col-6" label="Gravity Toolface" unit="&deg;" size={30}
                            value={formatNumber(reading?.highSide)}
                            accent={toolfaceColour('Highside')}
                            active={toolfaceType !== 'Steering'}
                        />
                    </div>

                    {toolQuiet && status !== 'stale' && (
                        <div className="alert alert-secondary mb-0 py-2 small">
                            {t('remote.toolQuiet', { seconds: Math.round(reading.readingAgeSeconds) })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
